import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import ContentService from '../services/content';
import contentActions from '../models/content/actions';
import { usePatronSelector } from './use-patron-selector';
import { contentSelectors } from '../models/content';
import type { Content } from '../models/content/types';
import {
  trackContentCardLike,
  trackContentCardLikeUpdate,
  trackContentCardUnlike,
} from '../models/content/analytics';
import { useEffect, useMemo } from 'react';
import { useSWRConfig } from 'swr';
import { getContentLikesDataKey } from './use-content-likes';
import { updateFeedContentCache } from './queries/update-feed-content-cache';

// Stores the user's selected reaction ID in the React Query cache so it is:
// - reactive across multiple mounted instances of the same content card
// - never stored inside expanded_reactions (keeps server cache clean)
// - NOT persisted to localStorage (meta.persist=false) — avoids async restoration race condition
// The seed-once useEffect guard prevents stale server reacted:true from re-lighting after unlike.
const reactionSelectionKey = (contentId: number) =>
  ['contentReactionSelection', contentId] as const;

type useExpandedReactionsProps = {
  contentId: number;
  analyticsData: IAnalyticsData;
};

export const useContentExpandedReactions = ({
  contentId,
  analyticsData,
}: useExpandedReactionsProps) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutate } = useSWRConfig();

  const reactions = usePatronSelector((state) =>
    contentSelectors.getContentReactions(state, { contentId })
  );
  const isLiked = usePatronSelector((state) =>
    contentSelectors.getContentLiked(state, { contentId })
  );
  const likeCount = usePatronSelector((state) =>
    contentSelectors.getContentLikeCount(state, { contentId })
  );
  const reactionCounts = usePatronSelector((state) =>
    contentSelectors.getContentReactionCounts(state, { contentId })
  );

  // selectedReactionId lives in the React Query cache — reactive (all useQuery observers on the
  // same key re-render when setQueryData is called) and shared across all mounted instances of
  // the same content card. meta.persist=false excludes this entry from PersistQueryClientProvider's
  // localStorage snapshot entirely (no localStorage growth). It is session-only: seeded on mount
  // from server data or the isLiked fallback, updated by user action, and garbage-collected after
  // 24h inactivity (gcTime global default in patron.jsx).
  // selectedReactionId is undefined before any setQueryData this session (not yet seeded),
  // null after an explicit unlike, or a reaction ID string after a like/switch.
  // We intentionally do NOT default to null here so we can distinguish "not seeded yet"
  // from "explicitly unset by unlike".
  const { data: selectedReactionId } = useQuery<string | null>({
    queryKey: reactionSelectionKey(contentId),
    queryFn: () => null, // never fetches; data set exclusively via setQueryData
    staleTime: Infinity,
    enabled: false,
    meta: { persist: false },
  });

  // Seed selectedReactionId into the React Query cache for cross-instance reactivity.
  // Only runs when the cache has never been written this session (alreadySet guard) so that:
  //   • in-session reaction switches aren't reset when feed refetches return stale reacted flags
  //   • replica-lag reacted:true can't re-light the button after a fresh unlike
  useEffect(() => {
    if (!reactions?.length) return;

    // alreadySet is true once setQueryData has written any value (including null) this session.
    const alreadySet =
      queryClient.getQueryState(reactionSelectionKey(contentId))?.data !==
      undefined;
    if (alreadySet) return;

    // Nothing set yet — seed from server if available, otherwise liked fallback.
    const serverSelected = reactions.find((r) => r.reacted);
    if (serverSelected && isLiked) {
      queryClient.setQueryData(
        reactionSelectionKey(contentId),
        serverSelected.id
      );
    } else if (isLiked) {
      // liked=true but reacted still false (replica lag) — default reaction as stand-in
      const defaultReaction = reactions.find((r) => r.default);
      queryClient.setQueryData(
        reactionSelectionKey(contentId),
        defaultReaction?.id ?? null
      );
    }
  }, [reactions, isLiked, contentId, queryClient]);

  // displayReactionId: synchronous version of the seed logic for immediate first-render display.
  // selectedReactionId is undefined before the useEffect above fires — using it directly would
  // cause a flash of "unliked" state on every page load even when liked:true is already in Redux.
  // When selectedReactionId has been explicitly set (null or a string), use it as-is.
  const displayReactionId = useMemo<string | null>(() => {
    if (selectedReactionId !== undefined) return selectedReactionId;
    // Not yet seeded — derive synchronously so the button renders correctly on first paint.
    const serverSelected = reactions?.find((r) => r.reacted);
    if (serverSelected && isLiked) return serverSelected.id;
    if (isLiked) return reactions?.find((r) => r.default)?.id ?? null;
    return null;
  }, [selectedReactionId, reactions, isLiked]);

  const selectedReaction = useMemo(
    () => reactions?.find((r) => r.id === displayReactionId) ?? null,
    [reactions, displayReactionId]
  );

  const availableReactions = useMemo(
    () =>
      reactions?.map((reaction) => ({
        id: reaction.id,
        label: reaction.title,
        emoji: reaction.emoji,
        default: reaction.default,
      })),
    [reactions]
  );

  const { mutate: setNewReaction, isError } = useMutation({
    mutationFn: (reactionId: string | null) => {
      if (!reactionId) {
        return new ContentService(contentId).unlike();
      }
      return new ContentService(contentId).like(reactionId);
    },
    onMutate: (reactionId) => {
      const prevSelectedReactionId = displayReactionId;
      // Snapshot Redux state for rollback in onError
      const prevReactions = reactions;
      const prevLikeCount = likeCount;

      // Update selection in cache (reactive)
      queryClient.setQueryData(reactionSelectionKey(contentId), reactionId);

      // Keep reaction count display responsive.
      // Delta: new like = +1, unlike = -1, switching reactions = 0
      const prevLiked = prevSelectedReactionId != null;
      const newLiked = reactionId != null;
      const delta =
        newLiked && !prevLiked ? 1 : !newLiked && prevLiked ? -1 : 0;

      // Update per-reaction counts and reacted flags for immediate UI feedback.
      const updatedReactions = reactions?.map((r) => ({
        ...r,
        reacted: r.id === reactionId,
        count:
          r.id === reactionId && r.id !== prevSelectedReactionId
            ? r.count + 1
            : r.id === prevSelectedReactionId && r.id !== reactionId
            ? Math.max(0, r.count - 1)
            : r.count,
      }));

      // Persist liked + counts + per-reaction counts to feed cache so addContents can
      // detect and guard against stale server data on reload (both liked-mismatch AND
      // count-mismatch cases — e.g. unlike where liked syncs fast but count lags).
      updateFeedContentCache(queryClient, contentId, {
        liked: !!reactionId,
        ...(delta !== 0 && {
          reaction_counts: { likes: likeCount + delta },
        }),
        ...(updatedReactions && {
          expanded_reactions: updatedReactions as Content['expanded_reactions'],
        }),
      });

      // Also update Redux liked directly so isLiked reflects the action immediately —
      // without this, updateFeedContentCache's propagation via addContents has a brief
      // delay that can let a stale server reacted:true re-light the button in useEffect.
      // Typed as Partial<Content> so a typo in _optimisticLikeAt (the field
      // addContents checks to decide whether this is fresher than an incoming
      // response) fails to compile instead of silently no-op-ing.
      const engagementUpdate: Partial<Content> = {
        liked: !!reactionId,
        ...(delta !== 0 && {
          reaction_counts: { ...reactionCounts, likes: likeCount + delta },
        }),
        ...(updatedReactions && {
          expanded_reactions: updatedReactions as Content['expanded_reactions'],
        }),
        _optimisticLikeAt: Date.now(),
      };
      dispatch(contentActions.updateContent(contentId, engagementUpdate));

      return { prevSelectedReactionId, prevReactions, prevLikeCount };
    },
    onSuccess: (data, reactionId, context) => {
      const { prevSelectedReactionId } = context ?? {};
      // Use context (previous selection) for analytics to avoid stale closure on selectedReaction
      if (!reactionId) {
        trackContentCardUnlike(contentId, {
          ...analyticsData,
          expanded_reaction_id: reactionId,
        });
      } else if (prevSelectedReactionId == null) {
        trackContentCardLike(contentId, {
          ...analyticsData,
          expanded_reaction_id: reactionId,
        });
      } else {
        trackContentCardLikeUpdate(contentId, {
          ...analyticsData,
          expanded_reaction_id: reactionId,
        });
      }
    },
    onError: (error, reactionId, context) => {
      console.error('Error in reaction mutation:', error);
      const { prevSelectedReactionId, prevReactions, prevLikeCount } =
        context ?? {};
      // Rollback selection and feed cache liked
      queryClient.setQueryData(
        reactionSelectionKey(contentId),
        prevSelectedReactionId ?? null
      );
      updateFeedContentCache(queryClient, contentId, {
        liked: prevSelectedReactionId != null,
        reaction_counts: { likes: prevLikeCount ?? likeCount },
        ...(prevReactions && {
          expanded_reactions: prevReactions as Content['expanded_reactions'],
        }),
      });
      // Rollback Redux optimistic updates (liked, counts, per-reaction counts).
      // Explicitly clear the marker onMutate stamped — plain `updateContent`
      // merges over the existing entry, so without this the marker survives
      // the rollback and keeps guarding a value that's now identical to the
      // server's for the rest of the window, discarding a genuinely newer
      // server-side change that arrives in that time.
      dispatch(
        contentActions.updateContent(contentId, {
          liked: prevSelectedReactionId != null,
          reaction_counts: {
            ...reactionCounts,
            likes: prevLikeCount ?? likeCount,
          },
          _optimisticLikeAt: undefined,
          ...(prevReactions && {
            expanded_reactions: prevReactions as Content['expanded_reactions'],
          }),
        })
      );
    },
    onSettled: (data, error, reactionId, context) => {
      // Trigger revalidation on the content likes list
      mutate(getContentLikesDataKey(contentId));
      reactionId && mutate(getContentLikesDataKey(contentId, reactionId));
      // Also revalidate the previous reaction's specific list (handles switch A→B and unlike)
      const { prevSelectedReactionId } = context ?? {};
      if (prevSelectedReactionId && prevSelectedReactionId !== reactionId) {
        mutate(getContentLikesDataKey(contentId, prevSelectedReactionId));
      }
    },
  });

  const handleSetNewReaction = (reactionId: string | null) =>
    reactionId !== displayReactionId && setNewReaction(reactionId);

  return {
    selectedReaction,
    availableReactions,
    setNewReaction: handleSetNewReaction,
    isError,
  };
};
